import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import Container from "../../../../connectui/component/layout/Container";
import {
  BaseChartTooltip,
  SegmentedBarChart,
} from "../../../../connectui/component/charts";

import DropDownSelect from "../../../../connectui/component/input/dropdown/DropDownSelect";
import OutlinedDropDownBase from "../../../../connectui/component/input/dropdown/base/OutlinedDropDownBase";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import {getDashboardMessageStats} from "../../../../redux/actions/dashboardActions";
import {
  getChannelColors,
  getChannelIcons,
  getSenderColors,
  getSenderIcons,
} from "../../../../utilities/helperFunctions";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import {useTranslation} from "react-i18next";
import Tooltip from "../../../../connectui/component/display/Tooltip";

const AUTOMATED_TYPES = ["chatbot", "auto_chatbot", "automessage"];

const MessageBreakdownCard = ({timeframe, listingGroups = null}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const data = useSelector((state) => state.dashboardReducer.messageData);
  const [filter, setFilter] = useState("By Type");
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();

  const colors = useMemo(
    () => ({
      channel: getChannelColors(),
      sender: getSenderColors(),
    }),
    [],
  );

  const icons = useMemo(
    () => ({
      channel: getChannelIcons(),
      sender: getSenderIcons(),
    }),
    [],
  );

  const currentStat = filter === "By Channels" ? "channel" : "sender";
  const stats = data?.[`${currentStat}_stats`] || [];
  const totalMessages = data?.total_messages || 0;

  const getData = (stat) => {
    if (!timeframe?.startDate || !timeframe?.endDate) return;

    setLoading(true);

    let isMounted = true;

    dispatch(
      getDashboardMessageStats({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        stat,
        listing_group_ids: listingGroups,
        onSuccess: () => {
          if (isMounted) setLoading(false);
        },
        onError: () => {
          if (isMounted) setLoading(false);
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    return getData(currentStat);
  }, [timeframe.startDate, timeframe.endDate, currentStat, listingGroups]);

  const processedData = useMemo(() => {
    if (!stats.length) return [];

    return stats
      .filter(
        (item) => !(currentStat === "sender" && item.sender === "other_user"),
      )
      .map((item) => {
        const key = item[currentStat];
        const value = item.num_of_messages;
        const percentage = (value / totalMessages) * 100;

        const getDisplay = (key, stat) => {
          if (stat === "channel") {
            return key === "SMS"
              ? "SMS"
              : key?.charAt(0).toUpperCase() + key?.slice(1);
          }
          const displayMap = {
            user: "Host",
            guest: "Guest",
            chatbot: "AI Copilot",
            auto_chatbot: "AI Autopilot",
          };
          return (
            displayMap[key] || key?.charAt(0).toUpperCase() + key?.slice(1)
          );
        };

        return {
          id: key,
          display: getDisplay(key, currentStat),
          value: percentage,
          count: value,
          color: colors[currentStat][key] || theme.colors.gray500,
          icon:
            icons[currentStat][key] ||
            (currentStat === "channel" ? "menu" : "user"),
        };
      });
  }, [stats, totalMessages, currentStat, colors, icons, theme.colors.gray500]);

  const automationRatio = useMemo(() => {
    if (!stats.length || currentStat !== "sender") return null;

    const displayedStats = stats.filter((item) => item.sender !== "other_user");

    const displayedTotal = displayedStats.reduce(
      (sum, item) => sum + (item.num_of_messages || 0),
      0,
    );

    const automatedMessages = displayedStats.reduce((sum, item) => {
      if (AUTOMATED_TYPES.includes(item.sender)) {
        return sum + (item.num_of_messages || 0);
      }
      return sum;
    }, 0);

    return displayedTotal > 0
      ? Number(((automatedMessages / displayedTotal) * 100).toFixed(1))
      : 0;
  }, [stats, currentStat]);

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <Container
            skeleton={{enabled: true, width: "100%", height: "30px"}}
          />
          <FlexContainer gap={theme.spacing(2)}>
            {[1, 2, 3].map((i) => (
              <Container
                key={i}
                skeleton={{enabled: true, width: "80px", height: "20px"}}
              />
            ))}
          </FlexContainer>
        </>
      );
    }

    if (!processedData.length) {
      return (
        <DashboardEmptyStateCard
          text={t("dashboard.message-breakdown.empty-state")}
          icon={ICONS["no-messages-white"]}
          height="300px"
        />
      );
    }

    return (
      <>
        {filter === "By Type" && (
          <FlexContainer
            alignItems="center"
            gap={theme.spacing(2)}
            sx={{marginBottom: theme.spacing(2)}}
          >
            <Text
              variant="displaySM"
              weight="semibold"
              skeleton={{enabled: loading, width: "80px", height: "32px"}}
            >
              {loading ? "" : `${automationRatio}%`}
            </Text>
            <FlexContainer alignItems="flex-end" gap={theme.spacing(1.5)}>
              <SvgIcon
                component={ICONS["ai"]}
                sx={{
                  width: "24px",
                  height: "24px",
                  color: theme.colors.purple500,
                }}
              />
              <Text
                variant="textMD"
                weight={"semibold"}
                color="textPrimary"
                sx={{marginBottom: "-5px"}}
              >
                Automation ratio
              </Text>
            </FlexContainer>
          </FlexContainer>
        )}
        <Container width="100%" height={60} sx={{padding: theme.spacing(2, 0)}}>
          <SegmentedBarChart
            data={processedData}
            settings={{
              height: 30,
              borderRadius: theme.radius.md,
              separation: 4,
              minBarWidth: 10,
              tooltip: {
                enabled: true,
                component: ({data, position, chartContainerRef}) => (
                  <BaseChartTooltip
                    position={position}
                    color={data.items[0]?.color}
                    items={data.items}
                    chartContainerRef={chartContainerRef}
                  />
                ),
              },
            }}
          />
        </Container>
        <FlexContainer
          width="100%"
          gap={theme.spacing(2)}
          sx={{flexWrap: "wrap", marginTop: theme.spacing(2)}}
        >
          {processedData.map((item, index) => (
            <FlexContainer
              key={index}
              alignItems="center"
              gap={theme.spacing(1)}
            >
              <SvgIcon
                component={ICONS[item.icon]}
                sx={{color: item.color, width: "15px", height: "15px"}}
              />
              <Text variant="textXS" color="textSecondary">
                {item.display}
              </Text>
            </FlexContainer>
          ))}
        </FlexContainer>
      </>
    );
  };

  return (
    <SimpleCard
      customContent={
        <FlexContainer
          width="100%"
          flexDirection="column"
          gap={theme.spacing(2)}
        >
          <FlexContainer
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <Text
                variant={"textMD"}
                color={"textSecondary"}
                weight={"medium"}
              >
                {t("dashboard.message-breakdown.title")}
              </Text>
              <Tooltip tooltipContent={t("dashboard.message-breakdown.help")}>
                <Container sx={{cursor: "help"}}>
                  <SvgIcon
                    component={ICONS["help-circle"]}
                    sx={{
                      width: "14px",
                      height: "14px",
                      color: theme.colors.gray400,
                    }}
                  />
                </Container>
              </Tooltip>
            </FlexContainer>
            <Container sx={{width: "120px"}}>
              <DropDownSelect
                dropDownBase={OutlinedDropDownBase}
                label={filter}
                selected={filter}
                onChange={setFilter}
                sx={{
                  label: {
                    padding: theme.spacing(1.5, 3.5),
                    minWidth: "120px",
                  },
                }}
              >
                <FlexContainer value="By Channels" label="By Channels">
                  <Text color={"textPrimary"}>By Channels</Text>
                </FlexContainer>
                <FlexContainer value="By Type" label="By Type">
                  <Text color={"textPrimary"}>By Type</Text>
                </FlexContainer>
              </DropDownSelect>
            </Container>
          </FlexContainer>
          {renderContent()}
        </FlexContainer>
      }
      sx={{height: "100%", width: "100%", backgroundColor: theme.colors.white}}
    />
  );
};

export default MessageBreakdownCard;
